import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { Focus } from '@goaltree/backend/convex/lib/focus'
import type { PlanAnswers } from '@goaltree/backend/convex/lib/onboardingFlow'
import type { TreeInput } from '@goaltree/backend/convex/nodes'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type GoalAnalysis = {
  goals: string[]
  // goal: index into `goals` the question is about; unset = the whole input.
  questions: Array<{ question: string; hint?: string; goal?: number }>
}

// A multi-goal input being planned one goal at a time: `queue` holds the titles
// still to plan after the one in preview.
export type SplitRun = {
  titles: string[]
  // Each title's index in `analysis.goals`, for its clarifying answers.
  parts: number[]
  queue: string[]
  index: number
  total: number
  source: string
}

interface OnboardingState {
  goalText: string
  generatedTree: TreeInput | null
  autoSaveAfterAuth: boolean
  isPublic: boolean
  // Explicit category picked on the input screen; null lets the AI choose.
  category: string | null
  planAnswers: PlanAnswers
  // Make it mine: the template the plan in preview was adapted from.
  templateId: Id<'templates'> | null
  // Tasks the user already has (read from a photo); the plan places each one.
  ownTasks: string[]
  // "What brings you here?" pick, held until it can be saved to prefs.
  focus: Focus | null
  // Split suggestion + clarifying questions for `goalText` (keyed by the text).
  analysis: (GoalAnalysis & { for: string }) | null
  // Clarifying answers by question.
  details: Record<string, string>
  detailsSkipped: boolean
  // 'split' plans `splitTitles` separately; 'one' keeps them as milestones.
  splitChoice: 'split' | 'one' | null
  splitTitles: string[]
  // Indexes of proposed goals the user unticked.
  splitOff: number[]
  splitRun: SplitRun | null
  setAnalysis: (v: (GoalAnalysis & { for: string }) | null) => void
  setDetail: (question: string, answer: string) => void
  setDetailsSkipped: (v: boolean) => void
  setSplitChoice: (v: 'split' | 'one' | null) => void
  setSplitTitles: (v: string[]) => void
  setSplitOff: (v: number[]) => void
  setSplitRun: (v: SplitRun | null) => void
  setGoalText: (goalText: string) => void
  setGeneratedTree: (tree: TreeInput | null) => void
  setAutoSaveAfterAuth: (v: boolean) => void
  setIsPublic: (v: boolean) => void
  setCategory: (v: string | null) => void
  setPlanAnswers: (v: PlanAnswers) => void
  setTemplateId: (v: Id<'templates'> | null) => void
  setOwnTasks: (v: string[]) => void
  setFocus: (v: Focus | null) => void
  claimAutoSave: () => TreeInput | null
  reset: () => void
}

const STORAGE_KEY = 'goaltree-onboarding'

// Another tab (e.g. the one a magic link opened) may already have claimed the
// pending save; its write lands in localStorage, not in this tab's memory.
function persistedAutoSave(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw)?.state?.autoSaveAfterAuth === true : true
  } catch {
    return true
  }
}

const initialState = {
  goalText: '',
  generatedTree: null,
  autoSaveAfterAuth: false,
  isPublic: true,
  category: null as string | null,
  planAnswers: {} as PlanAnswers,
  templateId: null as Id<'templates'> | null,
  ownTasks: [] as string[],
  focus: null as Focus | null,
  analysis: null as (GoalAnalysis & { for: string }) | null,
  details: {} as Record<string, string>,
  detailsSkipped: false,
  splitChoice: null as 'split' | 'one' | null,
  splitTitles: [] as string[],
  splitOff: [] as number[],
  splitRun: null as SplitRun | null,
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      ...initialState,
      setGoalText: (goalText) => set({ goalText }),
      setGeneratedTree: (generatedTree) =>
        set({
          generatedTree: generatedTree && {
            ...generatedTree,
            title: generatedTree.title.charAt(0).toUpperCase() + generatedTree.title.slice(1),
          },
        }),
      setAutoSaveAfterAuth: (autoSaveAfterAuth) => set({ autoSaveAfterAuth }),
      setIsPublic: (isPublic) => set({ isPublic }),
      setCategory: (category) => set({ category }),
      setPlanAnswers: (planAnswers) => set({ planAnswers }),
      setTemplateId: (templateId) => set({ templateId }),
      setOwnTasks: (ownTasks) => set({ ownTasks }),
      setFocus: (focus) => set({ focus }),
      setAnalysis: (analysis) =>
        set({
          analysis,
          details: {},
          detailsSkipped: false,
          splitChoice: null,
          splitTitles: analysis?.goals ?? [],
          splitOff: [],
        }),
      setDetail: (question, answer) => set({ details: { ...get().details, [question]: answer } }),
      setDetailsSkipped: (detailsSkipped) => set({ detailsSkipped }),
      setSplitChoice: (splitChoice) => set({ splitChoice }),
      setSplitTitles: (splitTitles) => set({ splitTitles }),
      setSplitOff: (splitOff) => set({ splitOff }),
      setSplitRun: (splitRun) => set({ splitRun }),
      // Atomically take the post-auth auto-save so only one caller saves the plan.
      claimAutoSave: () => {
        const { autoSaveAfterAuth, generatedTree } = get()
        if (!autoSaveAfterAuth || !generatedTree || !persistedAutoSave()) return null
        set({ autoSaveAfterAuth: false })
        return generatedTree
      },
      // Discarding a draft mid-onboarding mustn't re-ask what they came for.
      reset: () => set({ ...initialState, focus: get().focus }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        goalText: state.goalText,
        generatedTree: state.generatedTree,
        autoSaveAfterAuth: state.autoSaveAfterAuth,
        isPublic: state.isPublic,
        category: state.category,
        planAnswers: state.planAnswers,
        templateId: state.templateId,
        ownTasks: state.ownTasks,
        focus: state.focus,
        analysis: state.analysis,
        details: state.details,
        detailsSkipped: state.detailsSkipped,
        splitChoice: state.splitChoice,
        splitTitles: state.splitTitles,
        splitOff: state.splitOff,
        splitRun: state.splitRun,
      }),
    },
  ),
)

// The split goals still ticked, edited titles falling back to the proposal.
export function chosenSplit(): Array<{ title: string; part: number }> {
  const { analysis, splitTitles, splitOff } = useOnboardingStore.getState()
  return (analysis?.goals ?? []).flatMap((g, part) =>
    splitOff.includes(part) ? [] : [{ title: splitTitles[part]?.trim() || g, part }],
  )
}

export const chosenSplitTitles = () => chosenSplit().map((s) => s.title)

// Clarifying answers for the plan prompt (for one part of a split: the ones
// about that part or the whole input); none when skipped.
export function answeredDetails(part?: number): Array<{ question: string; answer: string }> {
  const { details, detailsSkipped, analysis } = useOnboardingStore.getState()
  if (detailsSkipped) return []
  const about = (q: string) => analysis?.questions.find((x) => x.question === q)?.goal
  return Object.entries(details)
    .filter(([q, a]) => a.trim() && (part === undefined || (about(q) ?? part) === part))
    .map(([question, answer]) => ({ question, answer }))
}

// Where a sign-in should land when a signed-out plan is waiting to be saved.
export function pendingPlanReturnTo(): string | undefined {
  const { autoSaveAfterAuth, generatedTree } = useOnboardingStore.getState()
  return autoSaveAfterAuth && generatedTree ? '/goals/new/preview' : undefined
}
